import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { InsulinDashboardService } from 'app/insulin-program/shared/insulin-dashboard.service';
import { InsulinHeaderFacilityService } from 'app/insulin-program/shared/insulin-header-facility.service';
import { rxWindowPercent, rxWindowUrgency, StatusSeverity } from 'app/insulin-program/shared/insulin-status';
import { BcdDashboardRxRowVm } from 'app/shared/generated/InsulinProgram/Models/BcdDashboardRxRowVm';
import { LinkComponent } from 'app/shared/link/link.component';
import { TagModule } from 'primeng/tag';
import { ResizableWidgetComponent } from '../resizable-widget/resizable-widget.component';

/**
 * Approved Insulin applications inside the QS1 reconciliation window.
 *
 * The urgency here is time: HOMER searches QS1 daily for a matching prescription, and once the
 * window closes an unmatched one has to be associated by hand. So each row shows which day of the
 * window it is on, from the server - the same numbers the reconciliation job works from, never
 * recomputed here, because two implementations of "which day are we on" disagree at the boundary and
 * the one on screen is the one somebody acts on.
 *
 * Reads the same response as the other two Insulin widgets through the shared service, so they
 * cannot show different snapshots of the same queue.
 *
 * Scoped to the facility selected in HOMER's header when it is one of the reader's Insulin
 * facilities, and to every facility they are associated with otherwise - the same default the intake
 * queue starts from. Each row names its own facility.
 */
@Component({
    selector: 'pcg-insulin-awaiting-rx-widget',
    imports: [
        ResizableWidgetComponent
        , LinkComponent
        , TagModule
    ],
    templateUrl: './insulin-awaiting-rx-widget.component.html',
    styleUrl: './insulin-awaiting-rx-widget.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsulinAwaitingRxWidgetComponent {

    // #region Injections
    private readonly dashboardService = inject(InsulinDashboardService);
    private readonly header = inject(InsulinHeaderFacilityService);
    private readonly destroyRef = inject(DestroyRef);
    // #endregion

    // #region State
    readonly isLoading = this.dashboardService.isLoading;
    readonly hasError = this.dashboardService.hasError;

    private readonly dashboard = this.dashboardService.dashboard;

    readonly isEnabled = computed(() => this.dashboard()?.isEnabled === true);
    readonly unavailableMessage = this.dashboardService.unavailableMessage;

    readonly count = computed(() => this.dashboard()?.awaitingRxAssociation ?? 0);

    readonly rows = computed(() => this.dashboard()?.awaitingRxSample ?? []);

    // #endregion

    // #region Row presentation

    /**
     * "Approved 08/21 &middot; e-Rx 08/30", as in the mockup.
     *
     * Built here rather than with pipes in the template because either date can be missing - a window
     * opened by a direct transition has no e-prescribed date - and the separator has to disappear
     * with the part it separates rather than leaving a dangling middot.
     */
    meta(row: BcdDashboardRxRowVm): string {
        const parts: string[] = [];
        const approved = this.shortDate(row.approvedUtc);
        const prescribed = this.shortDate(row.ePrescribedUtc);

        if (approved) { parts.push(`Approved ${approved}`); }
        if (prescribed) { parts.push(`e-Rx ${prescribed}`); }
        // Neither date recorded: name the facility instead, so the row is never left without context.
        if (parts.length === 0) { return row.facilityName ?? ''; }

        return parts.join(' · ');
    }

    /**
     * M/D in the reader's own timezone - unpadded, so 8/3 rather than 08/03.
     *
     * The API serialises DateTime with Kind=Unspecified, so the string carries no Z for the browser
     * to act on and `new Date(...)` would read a UTC clock as local. This applies the same offset
     * correction HOMER's utcToLocal pipe does.
     */
    private shortDate(value: Date | string | null | undefined): string | null {
        if (!value) { return null; }

        const raw = typeof value === 'string' ? value.replaceAll('Z', '') : value;
        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) { return null; }

        const local = new Date(parsed.getTime() - (parsed.getTimezoneOffset() * 60000));
        return `${local.getMonth() + 1}/${local.getDate()}`;
    }

    // #endregion

    /** How wide to draw the progress bar. Presentation only; the day count comes from the server. */
    percent(row: BcdDashboardRxRowVm): number {
        return rxWindowPercent(row.dayOfWindow, row.windowLengthDays);
    }

    /** Colour by how close the window is to closing, always beside the day count in text. */
    urgency(row: BcdDashboardRxRowVm): StatusSeverity {
        // A held row is waiting on a person, which is the widget's most urgent state whatever day it is.
        return row.isHeld ? 'warn' : rxWindowUrgency(row.dayOfWindow, row.windowLengthDays, row.isExpired);
    }

    /**
     * What a screen reader hears instead of the bar, which is decorative once this is read out.
     */
    windowLabel(row: BcdDashboardRxRowVm): string {
        if (row.isHeld) { return 'The automated search has stopped and needs review'; }
        return row.isExpired
            ? `Reconciliation window closed after ${row.windowLengthDays} days`
            : `Day ${row.dayOfWindow} of ${row.windowLengthDays} in the reconciliation window`;
    }
    // Asks again whenever the header facility changes. The figures follow it - that facility's
    // when it is one of the reader's Insulin facilities, all of theirs otherwise - which is the same
    // default the intake queue these link into starts from (InsulinHeaderFacilityService). Waits
    // until the header facility is known, so the first request is already the right one.
    private readonly followHeaderFacility = effect(() => {
        if (!this.header.ready()) { return; }
        this.header.facilityId();
        untracked(() => this.load());
    });

    /**
     * The status page rather than the review screen: these applications have already been decided,
     * so the timeline and the reconciliation panel are what somebody opening one needs. It re-checks
     * module and facility access against the record itself.
     */
    statusUrl(bcdProgramApplicationId: number): string {
        // The review page, opened on its QS1 tab: that is where the reconciliation lives now.
        return `insulin-program/intake-queue/review/${bcdProgramApplicationId}?tab=qs1`;
    }

    private load(): void {
        this.dashboardService
            .refresh()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }
}
